/**
 * HeaderUser — avatar + nom + menu deroulant logout.
 *
 * Affiche en bas de la <Sidebar>. Dropdown s'ouvre vers le haut (la sidebar
 * ancre par le bas). Click-outside ferme le menu.
 *
 * Adapte (simplifie) de scapino-portal/frontend/src/components/SidebarUserMenu.tsx —
 * sans role override (pas de notion de roles dans sgraphic) et sans
 * notification bell.
 */
import { useEffect, useRef, useState } from 'react'

import { useConfirm } from '@/contexts/confirm-dialog-context'
import Button from '@/components/ui/Button'
import { useAuth } from '@/hooks/useAuth'

import './HeaderUser.css'

export default function HeaderUser() {
  const { user, logout } = useAuth()
  const confirm = useConfirm()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  if (!user) return null

  const handleLogout = async () => {
    setOpen(false)
    if (
      await confirm.confirm({
        title: 'Se déconnecter',
        message: 'Voulez-vous vous déconnecter ?',
        confirmText: 'Déconnexion',
        cancelText: 'Annuler',
      })
    ) {
      await logout()
    }
  }

  const displayName = user.full_name ?? user.email
  const initial = (user.full_name ?? user.email).charAt(0).toUpperCase()

  return (
    <div className="header-user" ref={ref}>
      <button
        type="button"
        className="header-user-trigger"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Menu utilisateur"
      >
        <span className="header-user-avatar">{initial}</span>
        <span className="header-user-details">
          <span className="header-user-name">{displayName}</span>
          <span className="header-user-email">{user.email}</span>
        </span>
      </button>

      {open && (
        <div className="header-user-dropdown" role="menu">
          <Button variant="quiet" size="sm" onClick={handleLogout} className="header-user-logout">
            Déconnexion
          </Button>
        </div>
      )}
    </div>
  )
}
